import { useEffect, useRef, useState } from "react"
import { ENRICHMENT_FIELDS, type EnrichmentField, type EnrichmentFields } from "@shared/ProductEnrichment"
import { adminKeys } from "../../search-standalone/services/adminKeys"
import { useAdminSession } from "./AdminSignIn"
import "./ProductEnrichmentStudio.css"

export function ProductEnrichmentStudio({ storeUrl }: { storeUrl: string }) {
	const { signedIn } = useAdminSession()
	const [open, setOpen] = useState(false),
		[workspace, setWorkspace] = useState<any>(),
		[current, setCurrent] = useState<any>()
	const [fields, setFields] = useState<EnrichmentFields>({}),
		[dirty, setDirty] = useState(false),
		[busy, setBusy] = useState(false)
	const [error, setError] = useState(""),
		[notice, setNotice] = useState(""),
		[group, setGroup] = useState("Core"),
		[page, setPage] = useState(1),
		[query, setQuery] = useState("")
	const [collection, setCollection] = useState(""),
		[position, setPosition] = useState(0)
	const generation = useRef(0),
		touchStart = useRef<number>()
	const base = `${import.meta.env.VITE_BACKEND_URL || ""}/api/v1/search/enrichment`
	async function request(path: string, method = "GET", body?: unknown) {
		const [pathname, search] = path.split("?")
		const params = new URLSearchParams(search)
		params.set("store", storeUrl)
		if (collection && !params.has("collection")) params.set("collection", collection)
		const response = await fetch(`${base}${pathname}?${params}`, {
			method,
			headers: {
				"Content-Type": "application/json",
				...(await adminKeys.authHeaders()),
			},
			...(body ? { body: JSON.stringify(body) } : {}),
		})
		const result = await response.json()
		if (!response.ok) throw new Error(result.error || "Unable to load product")
		return result
	}
	function mayLeave() {
		return !dirty || window.confirm("Discard unsaved chip edits? Your saved draft will remain.")
	}
	async function loadProduct(product: any, selectedCollection: string, token: number) {
		const params = new URLSearchParams({
			store: storeUrl,
			collection: selectedCollection,
		})
		const response = await fetch(`${base}/products/${encodeURIComponent(product.id)}?${params}`, {
			headers: await adminKeys.authHeaders(),
		})
		const result = await response.json()
		if (!response.ok) throw new Error(result.error || "Unable to load product")
		if (token !== generation.current) return
		setCurrent(result)
		setFields(JSON.parse(JSON.stringify(result.review.fields)))
		setDirty(false)
		setNotice("")
	}
	async function load(nextPage = page, nextCollection = collection, nextQuery = query) {
		const token = ++generation.current
		setBusy(true)
		setError("")
		setCurrent(undefined)
		try {
			const params = new URLSearchParams({
				page: String(nextPage),
				q: nextQuery,
				...(nextCollection ? { collection: nextCollection } : {}),
			})
			const result = await request(`/workspace?${params}`)
			if (token !== generation.current) return
			setWorkspace(result)
			setCollection(result.collection)
			setPage(nextPage)
			setPosition(0)
			if (result.products.length) await loadProduct(result.products[0], result.collection, token)
		} catch (e: any) {
			if (token === generation.current) setError(e.message)
		} finally {
			if (token === generation.current) setBusy(false)
		}
	}
	useEffect(() => {
		setOpen(false)
		setWorkspace(undefined)
		setCurrent(undefined)
		setDirty(false)
		setCollection("")
		generation.current++
	}, [storeUrl, signedIn])
	useEffect(() => {
		const listener = (event: BeforeUnloadEvent) => {
			if (dirty) {
				event.preventDefault()
				event.returnValue = ""
			}
		}
		window.addEventListener("beforeunload", listener)
		return () => window.removeEventListener("beforeunload", listener)
	}, [dirty])
	if (!signedIn || !storeUrl) return null
	const keys = Object.keys(ENRICHMENT_FIELDS) as EnrichmentField[]
	const reviewed = Object.values(fields).filter((f) => f && f.state !== "suggested").length
	const product = current?.product
	const publicationPending = Boolean(
		current?.review.publication &&
			current.review.publication.status !== "complete" &&
			current.review.sourceHash === current.sourceHash,
	)
	const edit = (key: EnrichmentField, changes: any) => {
		setFields((old) => ({
			...old,
			[key]: {
				values: [],
				state: "suggested",
				evidence: "",
				...old[key],
				...changes,
			},
		}))
		setDirty(true)
		setNotice("")
	}
	async function navigate(delta: number) {
		const target = position + delta
		if (busy || target < 0 || target >= workspace.products.length || !mayLeave()) return
		setBusy(true)
		setError("")
		try {
			await loadProduct(workspace.products[target], collection, ++generation.current)
			setPosition(target)
		} catch (e: any) {
			setError(e.message)
		} finally {
			setBusy(false)
		}
	}
	async function save() {
		const record = await request(`/products/${encodeURIComponent(product.id)}`, "PUT", {
			revision: current.review.revision,
			sourceHash: current.sourceHash,
			fields,
			...(current.review.publication &&
			current.review.publication.status !== "complete" &&
			current.review.sourceHash !== current.sourceHash
				? { supersedePublication: true }
				: {}),
		})
		setCurrent((old: any) => ({ ...old, review: record, stale: false }))
		setFields(JSON.parse(JSON.stringify(record.fields)))
		setDirty(false)
		return record
	}
	async function suggest() {
		setBusy(true)
		setError("")
		try {
			const result = await request(`/products/${encodeURIComponent(product.id)}/suggest`, "POST", {})
			if (result.sourceHash !== current.sourceHash || result.revision !== current.review.revision)
				throw new Error("Product changed while AI was working. Reload before applying suggestions.")
			setFields((old) => {
				const next = { ...old }
				for (const [key, value] of Object.entries(result.fields)) {
					if (!next[key as EnrichmentField]) next[key as EnrichmentField] = value as any
				}
				return next
			})
			setDirty(true)
			setNotice("AI filled empty fields only. Verify each suggestion before reviewing.")
		} catch (e: any) {
			setError(e.message)
		} finally {
			setBusy(false)
		}
	}
	async function act(action: "save" | "publish") {
		setBusy(true)
		setError("")
		setNotice("")
		try {
			const record = dirty ? await save() : current.review
			if (action === "publish") {
				const published = await request(`/products/${encodeURIComponent(product.id)}/publish`, "POST", {
					revision: record.revision,
				})
				setCurrent((old: any) => ({ ...old, review: published }))
				setNotice(
					published.publication.status === "complete"
						? "Published to this product and its existing Shortlist copies."
						: "Some index updates failed. Your review is safe; retry publication below.",
				)
			} else setNotice("Draft saved. Search results have not changed.")
		} catch (e: any) {
			setError(e.message)
		} finally {
			setBusy(false)
		}
	}
	return (
		<section className="chip-entry">
			<div>
				<strong>Product data · Chip Studio</strong>
				<p>Review your catalogue one product at a time. Publishing updates search.</p>
			</div>
			<button
				disabled={busy}
				onClick={() => {
					if (open) {
						if (mayLeave()) {
							setOpen(false)
							generation.current++
						}
					} else {
						setOpen(true)
						void load()
					}
				}}
			>
				{open ? "Close studio" : "Open Chip Studio"}
			</button>
			{open && (
				<div className="chip-studio" aria-label="Chip Studio">
					<header className="chip-header">
						<div>
							<span className="chip-eyebrow">CATALOGUE PLAYGROUND</span>
							<h2>A little review. Better discovery.</h2>
							<p>Pick the chips that describe this product. Review each field, then publish.</p>
						</div>
						<div className="chip-score">
							<strong>{reviewed}</strong>
							<span>fields reviewed</span>
						</div>
					</header>
					<form
						className="chip-toolbar"
						onSubmit={(e) => {
							e.preventDefault()
							if (!busy && mayLeave()) void load(1)
						}}
					>
						<input
							aria-label="Find catalogue product"
							value={query}
							onChange={(e) => setQuery(e.target.value)}
							placeholder="Find a product…"
						/>
						<button disabled={busy}>Search</button>
						{workspace && (
							<select
								aria-label="Source collection"
								disabled={busy}
								value={collection}
								onChange={(e) => {
									if (mayLeave()) void load(1, e.target.value)
								}}
							>
								{workspace.collections.map((c: string) => (
									<option key={c}>{c}</option>
								))}
							</select>
						)}
						<span>{workspace?.found ?? "…"} products</span>
					</form>
					{error && (
						<p className="chip-error" role="alert">
							{error}{" "}
							<button
								disabled={busy}
								onClick={() => {
									if (mayLeave()) void load()
								}}
							>
								Reload
							</button>
						</p>
					)}
					{notice && (
						<p role="status" className="chip-notice">
							{notice}
						</p>
					)}
					{busy && <p role="status">Loading or saving product…</p>}
					{!busy && workspace?.products.length === 0 && <p>No products match. Try a different search.</p>}
					{product && (
						<>
							{current.stale && (
								<p className="chip-error">
									This product changed since the saved review. Recheck every saved claim against the current description
									before saving. Previous chips are shown for reference.{" "}
									{current.review.publication && current.review.publication.status !== "complete"
										? "Saving the new review will replace the unfinished publication; old claims will not be republished."
										: ""}{" "}
									<button
										onClick={() => {
											setFields((old) =>
												Object.fromEntries(Object.entries(old).map(([k, f]) => [k, { ...f, state: "suggested" }])),
											)
											setCurrent((old: any) => ({ ...old, stale: false }))
											setDirty(true)
										}}
									>
										Start review of updated product
									</button>
								</p>
							)}
							<div className="chip-layout">
								<aside className="chip-evidence">
									<span className="chip-eyebrow">SOURCE OF TRUTH</span>
									<h3>What the catalogue says</h3>
									<div className="chip-description">
										{String(product.description || product.raw_description || "No description supplied.").replace(
											/<[^>]*>/g,
											" ",
										)}
									</div>
									<details>
										<summary>Raw catalogue data</summary>
										<pre>
											{JSON.stringify(
												Object.fromEntries(
													Object.entries(product).filter(([key]) => !["embedding", "enrichment_keys"].includes(key)),
												),
												null,
												2,
											)}
										</pre>
									</details>
									<p className="chip-help">
										Price, stock, variants, photos and product links stay with the catalogue. Chips do not change them.
									</p>
								</aside>
								<div
									className="chip-product"
									onTouchStart={(e) => {
										touchStart.current = e.touches[0].clientX
									}}
									onTouchEnd={(e) => {
										if (touchStart.current !== undefined) {
											const delta = e.changedTouches[0].clientX - touchStart.current
											if (Math.abs(delta) > 70) void navigate(delta < 0 ? 1 : -1)
											touchStart.current = undefined
										}
									}}
								>
									<div className="chip-image">
										{product.image || product.images?.[0] ? (
											<img
												src={product.image || product.images[0]}
												alt={product.name || "Catalogue product"}
												referrerPolicy="no-referrer"
											/>
										) : (
											<span>No catalogue image</span>
										)}
									</div>
									<h3>{product.name}</h3>
									<p>
										{product.currency} {product.price} · {product.inStock ? "In stock" : "Out of stock"}
									</p>
									{/^https?:\/\//.test(product.url || "") && (
										<a href={product.url} target="_blank" rel="noreferrer">
											View original product ↗
										</a>
									)}
									<div className="chip-navigation">
										<button
											aria-label="Previous product"
											disabled={busy || position === 0}
											onClick={() => void navigate(-1)}
										>
											← Previous
										</button>
										<span>
											{position + 1} / {workspace.products.length}
										</span>
										<button
											aria-label="Next product"
											disabled={busy || position === workspace.products.length - 1}
											onClick={() => void navigate(1)}
										>
											Next →
										</button>
									</div>
									<p className="chip-help">Swipe to browse. Your review only counts when you save.</p>
								</div>
								<div className="chip-board">
									<div className="chip-tabs" role="tablist" aria-label="Attribute groups">
										{["Core", "Details", "Discovery"].map((g) => (
											<button key={g} role="tab" aria-selected={group === g} onClick={() => setGroup(g)}>
												{g}
												{g !== "Core" ? " · optional" : ""}
											</button>
										))}
									</div>
									<button
										disabled={busy || current.stale || publicationPending}
										className="chip-suggest"
										onClick={() => {
											setFields((old) => ({ ...current.suggestions, ...old }))
											setDirty(true)
											setNotice(
												"Catalogue suggestions added. They are unreviewed; check the description before approving.",
											)
										}}
									>
										＋ Fill empty fields from catalogue
									</button>
									<button
										disabled={busy || current.stale || publicationPending}
										className="chip-suggest"
										onClick={() => void suggest()}
									>
										✦ Suggest with AI · empty fields only
									</button>
									{keys
										.filter((key) => ENRICHMENT_FIELDS[key].group === group)
										.map((key) => {
											const field = fields[key] || {
												values: [],
												state: "suggested",
												evidence: "",
											}
											const options = [...new Set([...ENRICHMENT_FIELDS[key].values, ...field.values])]
											const singleValue = ["pockets", "lining", "stretch"].includes(key)
											return (
												<fieldset
													key={key}
													disabled={busy || publicationPending || current.stale}
													className="chip-field"
												>
													<legend>
														{key.replace("colour", "Colour")}{" "}
														<span className={`chip-state ${field.state}`}>{field.state.replace("_", " ")}</span>
													</legend>
													<div className="chip-options">
														{options.map((value) => (
															<button
																type="button"
																key={value}
																aria-pressed={field.values.includes(value)}
																className={field.values.includes(value) ? "selected" : ""}
																onClick={() =>
																	edit(key, {
																		values: field.values.includes(value)
																			? field.values.filter((v) => v !== value)
																			: singleValue
																			? [value]
																			: [...field.values, value],
																		state: "suggested",
																	})
																}
															>
																{field.values.includes(value) ? "✓ " : "＋ "}
																{value}
															</button>
														))}
													</div>
													<form
														onSubmit={(e) => {
															e.preventDefault()
															const input = e.currentTarget.elements.namedItem("custom") as HTMLInputElement
															const value = input.value.trim()
															if (value && !field.values.includes(value)) {
																edit(key, {
																	values: singleValue ? [value] : [...field.values, value],
																	state: "suggested",
																})
																input.value = ""
															}
														}}
													>
														<input
															name="custom"
															aria-label={`Add ${key} chip`}
															maxLength={80}
															placeholder="Add a specific value…"
														/>
														<button>Add</button>
													</form>
													<label className="chip-evidence-label">
														{["occasion", "aesthetic", "season"].includes(key)
															? "Why this fits (styling judgement)"
															: "Evidence from the catalogue or your verified product knowledge"}
														<textarea
															aria-label={`${key} evidence`}
															value={field.evidence}
															maxLength={1200}
															onChange={(e) =>
																edit(key, {
																	evidence: e.target.value,
																	state: "suggested",
																})
															}
														/>
													</label>
													<div className="chip-review">
														<button
															disabled={!field.values.length || !field.evidence.trim()}
															onClick={() => edit(key, { state: "reviewed" })}
														>
															✓ Reviewed
														</button>
														<button
															onClick={() =>
																edit(key, {
																	values: [],
																	state: "unknown",
																	evidence: "Not supplied or not verified",
																})
															}
														>
															Unknown
														</button>
														<button
															onClick={() =>
																edit(key, {
																	values: [],
																	state: "not_applicable",
																	evidence: "Does not apply to this product",
																})
															}
														>
															Not applicable
														</button>
													</div>
												</fieldset>
											)
										})}
								</div>
							</div>
							<footer className="chip-footer">
								<span>
									{dirty
										? "Unsaved edits"
										: current.review.revision
										? `Draft v${current.review.revision} saved`
										: "No saved draft yet"}
									{current.review.published ? ` · Published v${current.review.published.revision}` : " · Not published"}
								</span>
								<div>
									<button
										disabled={busy || !dirty || current.stale || publicationPending}
										onClick={() => void act("save")}
									>
										Save draft
									</button>
									<button
										className="chip-publish"
										disabled={busy || !reviewed || current.stale}
										onClick={() => void act("publish")}
									>
										{current.review.publication?.status === "partial" ||
										current.review.publication?.status === "pending"
											? "Retry publication"
											: "Publish reviewed fields"}
									</button>
								</div>
							</footer>
							{current.review.publication && (
								<details className="chip-targets" open={current.review.publication.status !== "complete"}>
									<summary>Index updates · {current.review.publication.status}</summary>
									{current.review.publication.targets.map((target: any) => (
										<p key={`${target.collection}/${target.id}`}>
											{target.collection} · {target.status}
											{target.error ? ` — ${target.error}` : ""}
										</p>
									))}
								</details>
							)}
						</>
					)}
					<div className="chip-pagination">
						<button
							disabled={busy || page <= 1}
							onClick={() => {
								if (mayLeave()) void load(page - 1)
							}}
						>
							Previous page
						</button>
						<span>Page {page}</span>
						<button
							disabled={busy || !workspace || page * 24 >= workspace.found}
							onClick={() => {
								if (mayLeave()) void load(page + 1)
							}}
						>
							Next page
						</button>
					</div>
				</div>
			)}
		</section>
	)
}
